import { ReactNode } from "react";
import { Box, Typography, Button, Alert, Skeleton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

interface IDashboardSectionContainerProps {
  title: string;
  loading: boolean;
  error: string | null;
  empty?: boolean;
  onRetry?: () => void;
  skeletonHeight?: number;
  children: ReactNode;
  // Acción opcional a la derecha del título (ej. botón "Ver detalle" de
  // Figma). No cambia la responsabilidad del contenedor (loading/error/empty),
  // solo agrega un slot de layout.
  headerAction?: ReactNode;
  // Oculta el título propio cuando la sección va anidada dentro de un
  // DashboardSectionGroup que ya muestra un heading compartido, sin perder el
  // manejo de loading/error/empty.
  hideTitle?: boolean;
  // Figma envuelve casi todos los paneles en una tarjeta ("div.bg-card":
  // fondo blanco, borde suave, esquinas redondeadas) — por default este
  // contenedor la aplica. Las únicas excepciones son los paneles cuyo
  // contenido ya son varias KpiCard (que ya traen su propia tarjeta
  // individual): ahí se pasa `disableCard` para no anidar una tarjeta dentro
  // de otra.
  disableCard?: boolean;
  // Alto fijo (px) de la tarjeta, tomado literal de Figma, para las filas
  // donde varias tarjetas hermanas van una al lado de la otra (ej. las 3 de
  // "Estado general de la operación"). No depender del stretch automático
  // del contenedor (Grid/flex) evita que, con contenido más corto que sus
  // vecinos, la tarjeta termine con un alto ambiguo entre navegadores.
  minHeight?: number;
}

const SectionSkeleton = ({ height }: { height: number }) => (
  <Box>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={height}
      sx={{ borderRadius: 1 }}
    />
  </Box>
);

const CARD_SX = {
  backgroundColor: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 2,
  p: { xs: 2, sm: 3 },
  // minWidth:0 + overflow:hidden evitan que un contenido ancho (ej. una
  // tabla) se salga de la tarjeta y se superponga con la sección vecina en
  // vez de scrollear internamente (el scroll interno lo resuelve cada
  // sección, ej. Geography.tsx, con su propio overflowX:auto).
  minWidth: 0,
  overflow: "hidden",
};

const DashboardSectionContainer = ({
  title,
  loading,
  error,
  empty = false,
  onRetry,
  skeletonHeight = 160,
  children,
  headerAction,
  hideTitle = false,
  disableCard = false,
  minHeight,
}: IDashboardSectionContainerProps) => (
  // Sin margen propio: el ritmo vertical entre secciones lo maneja quien
  // agrupa (DashboardSectionGroup / MetricsDashboardContent) con un único
  // spacing de 24px (ver Figma: el gap es uniforme en todo el dashboard).
  // Un mb acá se sumaría al de esos wrappers y desalinearía todo.
  //
  // Sin `minHeight` explícito NO se fuerza `height: 100%`: una tarjeta sola
  // en su fila (sin hermana con la que igualar alto) no tiene contra qué
  // resolver ese 100% — contra un contenedor de alto automático, el
  // navegador puede terminar agrandándola más de lo que su contenido
  // necesita, pisando el título del próximo grupo. Sin ninguno de los dos
  // props, la tarjeta simplemente se ajusta a su contenido.
  <Box
    sx={{
      ...(minHeight ? { minHeight } : {}),
      ...(disableCard ? {} : CARD_SX),
    }}
  >
    {!hideTitle && (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        mb={1.5}
      >
        {/* Figma: "h3.text-sm" — 14px, no un h6 grande. */}
        <Typography
          variant="body2"
          fontWeight={600}
          color="text.primary"
          sx={{ fontSize: "0.875rem" }}
        >
          {title}
        </Typography>
        {headerAction}
      </Box>
    )}
    {loading && <SectionSkeleton height={skeletonHeight} />}
    {!loading && error && (
      <Alert
        severity="error"
        action={
          onRetry && (
            <Button
              startIcon={<RefreshIcon />}
              size="small"
              onClick={onRetry}
              color="inherit"
            >
              Reintentar
            </Button>
          )
        }
      >
        {error}
      </Alert>
    )}
    {!loading && !error && empty && (
      <Alert severity="info">No hay datos para el período seleccionado.</Alert>
    )}
    {!loading && !error && !empty && children}
  </Box>
);

export default DashboardSectionContainer;
